import { Grid2 } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { Loader, PaymentOptionCard, PaymentToolBar } from 'components';
import { UserInformation } from 'components/UserInformation/UserInformation';
import { config } from 'config/config';
import { useBranchContext } from 'context/BranchesContext';
import { useUserSearchContext } from 'context/UserSearchContext';
import {
  useCreateBooking,
  usePutBookingAddons,
  usePutBookingPromoCode,
} from 'hooks/useBooking';
import React, { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { CreateBookingPayload } from 'types/BookingCreate';
import {
  ExtrasRentalRate,
  RentalRate,
  UpdateDetailApiPayload,
} from 'types/BookingDetails';

export const PaymentOption: React.FC = () => {
  const { bookingDetailIds } = useParams();

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [paymentType, setPaymentType] = useState<number>(1); // 1 pre pay, 0 pay later
  const queryClient = useQueryClient();
  const { mutate: submitDetail, isPending: isPendingDetail } =
    usePutBookingAddons();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const locationCode = searchParams.get('locationCode');
  const { bookingSearchData, setBookingSearchData, loading } =
    useUserSearchContext();
  const { kioskLocation } = useBranchContext();

  const handleCheck = (value: number) => {
    if (value === paymentType) return;
    bookingSearchData.chargeCodes = bookingSearchData.chargeCodes.includes(
      'PPD'
    )
      ? bookingSearchData.chargeCodes.filter((data: string) => data !== 'PPD')
      : [...bookingSearchData.chargeCodes, 'PPD'];

    const payload: UpdateDetailApiPayload = {
      DetailId: bookingSearchData.SearchDetailId,
      Duration: bookingSearchData.RentalDays,
      Mileage: 0,
      Modelyear: 0,
      ChargeCodes: [...bookingSearchData.chargeCodes],
      Qty: 1,
    };

    submitDetail(payload, {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [
            'getBookingDetail',
            bookingSearchData.SearchDetailId,
            bookingSearchData.VehicleModel.VehicleId,
          ],
        });
      },
    });

    setBookingSearchData((prev) => ({
      ...prev,
      PromoCodeDis: bookingSearchData?.PromoCodeDis,
      PromoCode: bookingSearchData?.PromoCode,
      chargeCodes: [...bookingSearchData.chargeCodes],
      paymentStatus: value,
      RentalRate: bookingSearchData.RentalRate.map((rentalRate: RentalRate) =>
        rentalRate.Code === 'PPD'
          ? {
              ...rentalRate,
              IsSelected: bookingSearchData.chargeCodes.includes('PPD')
                ? true
                : false,
            }
          : { ...rentalRate }
      ),
    }));
    setPaymentType(value);
  };

  const prePaidPrice: RentalRate = bookingSearchData.RentalRate.find(
    (rentalRate: RentalRate) => rentalRate.Code === 'PPD'
  );

  const { mutate: finalSubmit, isPending } = useCreateBooking();

  const continueHandle = async () => {
    const payload: CreateBookingPayload = {
      Id: bookingSearchData.SearchDetailId,
      Email: bookingSearchData.Renter.EmailId,
      IsAbove21: bookingSearchData.Renter.IsAbove21,
      IsSignUp: bookingSearchData.Renter.IsSignUp ?? false,
      IsTCAccepted: bookingSearchData.Renter.IsTCAccepted,
      FirstName: bookingSearchData.Renter.FirstName,
      LastName: bookingSearchData.Renter.LastName,
      MobileNo: Number(bookingSearchData.Renter.ContactNo),
      Nationality: bookingSearchData.Renter.Nationality,
      VisaStatus: bookingSearchData.Renter.VisaStatus,
      BookingOrigin: 1,
      BookingSource: `Kiosk - ${kioskLocation.Code}`,
      PaymentMethod: 0,
    };

    finalSubmit(payload, {
      onSuccess: (data) => {
        if (data.result?.IsValid) {
          if (data.result?.PaymentUrl) {
            setBookingSearchData((prev) => ({
              ...prev,
              paymentUrl: data.result.PaymentUrl,
            }));
            if (!loading) {
              navigate(
                `/pay-amount/${data.result.ID}?locationCode=${locationCode}`
              );
            }
          } else {
            navigate(
              `/confirmed-booking/${data.result?.ID}/${data.result.IsValid}?locationCode=${locationCode}`
            );
          }
        }
      },
    });
  };

  const validRentals =
    bookingSearchData?.RentalRate?.filter(
      (rental: RentalRate) =>
        // bookingSearchData?.chargeCodes?.includes(rental.Code) &&
        ['TAR', 'DIT', 'PPD'].includes(rental.Code)
      // ['TAR', 'DIT'].includes(rental.Code)
    ) || [];

  const totalAmount = validRentals.reduce(
    (sum: number, rental: RentalRate) => sum + (rental?.TotalAmt || 0),
    0
  );

  const discount = bookingSearchData?.PromoCodeDis ?? 0;

  const finalAmount =
    discount > 0 ? totalAmount * (1 - discount / 100) : totalAmount;

  // Insurance Total
  const insuranceTotal =
    bookingSearchData?.RentalRate?.reduce((sum: number, rate: RentalRate) => {
      if (
        bookingSearchData.chargeCodes.includes(rate.Code) &&
        config.insuranceChargeCode.includes(rate.Code)
      ) {
        return sum + (rate.TotalAmt || 0);
      }
      return sum;
    }, 0) || 0;

  // Ancillaries Total
  const ancillariesTotal =
    bookingSearchData?.RentalRate?.reduce((sum: number, rate: RentalRate) => {
      if (
        bookingSearchData.chargeCodes.includes(rate.Code) &&
        config.ancilarriesChargeCode.includes(rate.Code)
      ) {
        return sum + (rate.TotalAmt || 0);
      }
      return sum;
    }, 0) || 0;

  // Extras Total
  const extrasTotal =
    bookingSearchData?.ExtrasRentalRate?.reduce(
      (sum: number, extra: ExtrasRentalRate) => {
        if (bookingSearchData.chargeCodes.includes(extra.Code)) {
          return sum + (extra.TotalAmt || 0);
        }
        return sum;
      },
      0
    ) || 0;

  const grandTotal =
    finalAmount + insuranceTotal + ancillariesTotal + extrasTotal;

  // const {
  //   data: getBookingDetails,
  // } = useGetBookingCheckout(bookingDetailIds!);

  const { mutate: applyPromoCode } = usePutBookingPromoCode();

  React.useEffect(() => {
    if (bookingSearchData?.PromoCode && bookingDetailIds) {
      applyPromoCode({
        id: bookingDetailIds,
        code: bookingSearchData.PromoCode,
      });
    }
  }, [bookingSearchData?.PromoCode, bookingDetailIds]);

  if (loading || isPending || isPendingDetail) return <Loader />;

  return (
    <>
      {isOpen ? (
        <UserInformation
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          onConsent={() => {
            setIsOpen(false);
            continueHandle();
          }}
        />
      ) : (
        <>
          <Grid2
            container
            spacing={2}
            height={'calc(100vh - 200px)'}
            sx={{
              justifyContent: 'center',
              alignItems: 'center',
              flexDirection: 'row-reverse',
            }}
          >
            <Grid2 size={{ md: 4 }}>
              <PaymentOptionCard
                id="1"
                title="Pay Now"
                description="Pay online with your credit / debit card"
                price={Number(grandTotal.toFixed(2))}
                isChecked={paymentType === 1 ? true : false}
                onCheck={() => handleCheck(1)}
                isLabel={true}
                PayNowDiscAmount={Math.abs(prePaidPrice.TotalAmt) || 0}
                numberOfDays={bookingSearchData.RentalDays}
              />
            </Grid2>
            <Grid2 size={{ md: 4 }}>
              <PaymentOptionCard
                id="0"
                title="Pay Later"
                description="Pay at counter while you pick the car"
                price={Number(
                  (
                    Number(grandTotal.toFixed(2)) +
                    Math.abs(prePaidPrice?.TotalAmt)
                  ).toFixed(2)
                )}
                isChecked={paymentType === 0 ? true : false}
                onCheck={() => handleCheck(0)}
                isLabel={false}
                numberOfDays={bookingSearchData.RentalDays}
              />
            </Grid2>
          </Grid2>
          <PaymentToolBar onClick={() => setIsOpen(true)} />
        </>
      )}
    </>
  );
};
